import {
  MarkerLabelConfigTypeEnum,
  MarkerLabelFieldTypeEnum,
  PointOfInterest,
} from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { abyssColors } from '@/theme/colors';
import { formatEnumForDisplay } from '@/utils';
import { Add, Edit } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { selectedPOIType } from '../../state';
import { dropDownStyles } from '../styles';
import { CustomSwitch, FullWidthPanel } from './styles';
import { PointOfInterestMarkerLabel } from '../../../shared/PointOfInterestMarkerLabel';

const blisterFields = [
  {
    key: 'maxHeight',
    name: 'Height',
  },
  {
    key: 'wallLoss',
    name: 'Estimated Wall Loss',
  },
  {
    key: 'residualCorrosionAllowance',
    name: 'Residual Corrosion Allowance',
  },
  {
    key: 'residualThickness',
    name: 'Residual Thickness',
  },
];

const pointOfInterestFields = [
  {
    key: 'poiId',
    name: 'POI ID',
  },
  {
    key: 'name',
    name: 'Name',
  },
  {
    key: 'paintRegion',
    name: 'Paint Block',
  },
  {
    key: 'assembly',
    name: 'Component',
  },
  {
    key: 'status',
    name: 'Status',
  },
  {
    key: 'visibility',
    name: 'Access Control',
  },
  {
    key: 'assignee',
    name: 'Assigned To',
  },
  {
    key: 'comment',
    name: 'Comment',
  },
  {
    key: 'workpack',
    name: 'Workpack',
  },
  {
    key: 'createdBy',
    name: 'Created By',
  },
  {
    key: 'accessibility',
    name: 'Accessibility',
  },
  {
    key: 'deckRelativeHeight',
    name: 'POI Height from Deck',
  },
  {
    key: 'viewpointRelativeHeight',
    name: 'POI Height from Viewpoint',
  },
];

export const PoiMarkerLabelPanel = ({
  handleSaveTemplate,
  examplePoi,
}: {
  handleSaveTemplate: () => void;
  examplePoi: PointOfInterest | undefined;
}) => {
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [headerFieldEnabled, setHeaderFieldEnabled] = useState<boolean>(
    !!selectedType?.markerLabelConfig?.headerField
  );

  const handleTypeChange = () => {
    setSelectedType({
      ...selectedType,
      ...(selectedType?.markerLabelConfig
        ? {
            markerLabelConfig: {
              ...selectedType?.markerLabelConfig,
              type:
                selectedType?.markerLabelConfig?.type === MarkerLabelConfigTypeEnum.Detailed
                  ? MarkerLabelConfigTypeEnum.Compact
                  : MarkerLabelConfigTypeEnum.Detailed,
            },
          }
        : {
            markerLabelConfig: {
              type: MarkerLabelConfigTypeEnum.Compact,
              hideName: false,
              fields: [],
              headerField: undefined,
            },
          }),
    });
  };

  const handleHideNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType({
      ...selectedType,
      ...(selectedType?.markerLabelConfig
        ? {
            markerLabelConfig: {
              ...selectedType?.markerLabelConfig,
              hideName: event.target.checked,
            },
          }
        : {
            markerLabelConfig: {
              type: MarkerLabelConfigTypeEnum.Compact,
              hideName: event.target.checked,
              fields: [],
              headerField: undefined,
            },
          }),
    });
  };

  const handleHeaderFieldEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHeaderFieldEnabled(event.target.checked);
    setSelectedType({
      ...selectedType,
      ...(selectedType?.markerLabelConfig
        ? {
            markerLabelConfig: {
              ...selectedType?.markerLabelConfig,
              ...(event.target.checked && selectedType?.markerLabelConfig?.headerField
                ? {
                    headerField: {
                      ...selectedType?.markerLabelConfig?.headerField,
                    },
                  }
                : { headerField: undefined }),
            },
          }
        : {
            markerLabelConfig: {
              type: MarkerLabelConfigTypeEnum.Compact,
              hideName: false,
              fields: [],
              ...(event.target.checked && selectedType?.markerLabelConfig?.headerField
                ? {
                    headerField: {
                      ...selectedType?.markerLabelConfig?.headerField,
                    },
                  }
                : { headerField: undefined }),
            },
          }),
    });
  };

  const fieldTypes = useMemo(() => {
    return Object.values(MarkerLabelFieldTypeEnum).map((type) => formatEnumForDisplay(type));
  }, []);

  const allTemplateFeilds = useMemo(() => {
    return selectedType?.fields ?? [];
  }, [selectedType]);

  const getFieldFromKey = (key: string) => {
    return allTemplateFeilds.find((field) => field.key === key);
  };

  return (
    <>
      <Stack
        width="100%"
        marginTop="1.2rem"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="4.8rem"
      >
        <Typography fontSize="2.2rem" fontWeight={500}>
          {`Marker Label Configuration for ${selectedType?.name}`}
        </Typography>
        {mode === 'Edit' ? (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              onClick={() => {
                setMode('ReadOnly');
                handleSaveTemplate();
              }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setMode('ReadOnly');
              }}
            >
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
            <Tooltip title="Edit Marker Label Configuration" placement="top" arrow>
              <IconButton size="medium" onClick={() => setMode('Edit')}>
                <Edit
                  fontSize="inherit"
                  sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <FullWidthPanel>
        <Stack sx={{ flex: 1, gap: '3.2rem' }}>
          <Grid container maxWidth="60rem" spacing={3}>
            <Grid size={6}>
              <Typography variant="h5">Marker Label size</Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body2">Compact</Typography>
                <FormControlLabel
                  sx={{ margin: 0 }}
                  control={
                    <CustomSwitch
                      disabled={mode === 'ReadOnly'}
                      checked={
                        selectedType?.markerLabelConfig?.type === MarkerLabelConfigTypeEnum.Detailed
                      }
                      onChange={handleTypeChange}
                      size="small"
                    />
                  }
                  label=""
                />
                <Typography variant="body2">Detailed</Typography>
              </Stack>
            </Grid>
            <Grid size={6}>
              <Typography variant="h5">Hide Name</Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body2">No</Typography>
                <FormControlLabel
                  sx={{ margin: 0 }}
                  control={
                    <CustomSwitch
                      disabled={mode === 'ReadOnly'}
                      checked={selectedType?.markerLabelConfig?.hideName ?? false}
                      onChange={handleHideNameChange}
                      size="small"
                    />
                  }
                  label=""
                />
                <Typography variant="body2">Yes</Typography>
              </Stack>
            </Grid>
          </Grid>
          <Stack gap={1}>
            <Typography variant="h4">Header Field</Typography>
            <Stack direction="row" alignItems="center" gap={4} width="100%">
              <Stack>
                <Typography variant="h5">Enable</Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="body2">No</Typography>
                  <FormControlLabel
                    sx={{ margin: 0 }}
                    control={
                      <CustomSwitch
                        disabled={mode === 'ReadOnly'}
                        checked={headerFieldEnabled}
                        onChange={handleHeaderFieldEnabledChange}
                        size="small"
                      />
                    }
                    label=""
                  />
                  <Typography variant="body2">Yes</Typography>
                </Stack>
              </Stack>

              {headerFieldEnabled && (
                <>
                  <Stack direction="row" alignItems="center" gap={2} width="30rem">
                    <Typography variant="h5">Type</Typography>
                    <Autocomplete
                      id="headerField-type"
                      disableClearable
                      disabled={mode === 'ReadOnly'}
                      options={fieldTypes}
                      value={selectedType?.markerLabelConfig?.headerField?.type ?? undefined}
                      onChange={(_, value) => {
                        if (value) {
                          const enumValue =
                            Object.values(MarkerLabelFieldTypeEnum).find(
                              (v) => v === value.toUpperCase().replaceAll(' ', '_')
                            ) ?? MarkerLabelFieldTypeEnum.TemplateField;
                          setSelectedType({
                            ...selectedType,
                            ...(selectedType?.markerLabelConfig
                              ? {
                                  markerLabelConfig: {
                                    ...selectedType?.markerLabelConfig,
                                    headerField: {
                                      ...selectedType?.markerLabelConfig?.headerField,
                                      type: enumValue,
                                      key: selectedType?.markerLabelConfig?.headerField?.key ?? '',
                                    },
                                  },
                                }
                              : {
                                  markerLabelConfig: {
                                    type:
                                      selectedType?.markerLabelConfig?.type ??
                                      MarkerLabelConfigTypeEnum.Compact,
                                    hideName: selectedType?.markerLabelConfig?.hideName ?? false,
                                    fields: [],
                                    headerField: undefined,
                                  },
                                }),
                          });
                        }
                      }}
                      getOptionLabel={(option) => formatEnumForDisplay(option)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="standard" placeholder="" />
                      )}
                      sx={{
                        ...dropDownStyles,
                        width: '100%',
                      }}
                    />
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={2} width="40rem">
                    <Typography variant="h5">Field</Typography>
                    <Autocomplete
                      id="headerField-key"
                      disableClearable
                      key={selectedType?.markerLabelConfig?.headerField?.type}
                      disabled={mode === 'ReadOnly'}
                      options={
                        selectedType?.markerLabelConfig?.headerField?.type ===
                        MarkerLabelFieldTypeEnum.Blister
                          ? blisterFields
                          : selectedType?.markerLabelConfig?.headerField?.type ===
                              MarkerLabelFieldTypeEnum.PointOfInterest
                            ? pointOfInterestFields
                            : allTemplateFeilds
                      }
                      value={
                        selectedType?.markerLabelConfig?.headerField?.type ===
                        MarkerLabelFieldTypeEnum.Blister
                          ? blisterFields.find(
                              (field) =>
                                field.key === selectedType?.markerLabelConfig?.headerField?.key
                            )
                          : selectedType?.markerLabelConfig?.headerField?.type ===
                              MarkerLabelFieldTypeEnum.PointOfInterest
                            ? pointOfInterestFields.find(
                                (field) =>
                                  field.key === selectedType?.markerLabelConfig?.headerField?.key
                              )
                            : getFieldFromKey(
                                selectedType?.markerLabelConfig?.headerField?.key ?? ''
                              )
                      }
                      getOptionLabel={(option) => formatEnumForDisplay(option.name)}
                      onChange={(_, value) => {
                        setSelectedType({
                          ...selectedType,
                          ...(selectedType?.markerLabelConfig
                            ? {
                                markerLabelConfig: {
                                  ...selectedType?.markerLabelConfig,
                                  headerField: {
                                    ...selectedType?.markerLabelConfig?.headerField,
                                    key: value?.key ?? '',
                                  },
                                },
                              }
                            : {
                                markerLabelConfig: {
                                  type:
                                    selectedType?.markerLabelConfig?.type ??
                                    MarkerLabelConfigTypeEnum.Compact,
                                  hideName: selectedType?.markerLabelConfig?.hideName ?? false,
                                  fields: [],
                                  headerField: undefined,
                                },
                              }),
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="standard" placeholder="" />
                      )}
                      sx={{
                        ...dropDownStyles,
                        width: '100%',
                      }}
                    />
                  </Stack>
                </>
              )}
            </Stack>
          </Stack>
          <Stack gap={1}>
            <Typography variant="h4">Fields</Typography>
            <Stack gap={4} width="100%" marginTop={1}>
              {selectedType?.markerLabelConfig?.fields?.map((field, index) => (
                <Stack key={`field-${index}-${field.key}-${field.type}`} direction="row">
                  <Stack direction="row" alignItems="center" gap={2} width="30rem">
                    <Typography variant="h5">Type</Typography>
                    <Autocomplete
                      id={`field-type-${index}-${field.type}`}
                      disableClearable
                      disabled={mode === 'ReadOnly'}
                      options={fieldTypes}
                      value={field.type ?? undefined}
                      onChange={(_, value) => {
                        if (value) {
                          const enumValue =
                            Object.values(MarkerLabelFieldTypeEnum).find(
                              (v) => v === value.toUpperCase().replaceAll(' ', '_')
                            ) ?? MarkerLabelFieldTypeEnum.TemplateField;
                          setSelectedType({
                            ...selectedType,
                            ...(selectedType?.markerLabelConfig
                              ? {
                                  markerLabelConfig: {
                                    ...selectedType?.markerLabelConfig,
                                    fields: (selectedType?.markerLabelConfig?.fields ?? [])?.map(
                                      (field, i) => {
                                        if (i === index) {
                                          return {
                                            ...field,
                                            type: enumValue,
                                          };
                                        }
                                        return field;
                                      }
                                    ),
                                  },
                                }
                              : {
                                  markerLabelConfig: {
                                    type:
                                      selectedType?.markerLabelConfig?.type ??
                                      MarkerLabelConfigTypeEnum.Compact,
                                    hideName: selectedType?.markerLabelConfig?.hideName ?? false,
                                    fields: [],
                                    headerField: undefined,
                                  },
                                }),
                          });
                        }
                      }}
                      getOptionLabel={(option) => formatEnumForDisplay(option)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="standard" placeholder="" />
                      )}
                      sx={{
                        ...dropDownStyles,
                        width: '100%',
                      }}
                    />
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={2} width="40rem">
                    <Typography variant="h5">Field</Typography>
                    <Autocomplete
                      id={`headerField-key-${index}-${field.key}`}
                      disableClearable
                      disabled={mode === 'ReadOnly'}
                      options={
                        field.type === MarkerLabelFieldTypeEnum.Blister
                          ? blisterFields
                          : field.type === MarkerLabelFieldTypeEnum.PointOfInterest
                            ? pointOfInterestFields
                            : allTemplateFeilds
                      }
                      value={
                        field.type === MarkerLabelFieldTypeEnum.Blister
                          ? blisterFields.find((blisterField) => blisterField.key === field.key)
                          : field.type === MarkerLabelFieldTypeEnum.PointOfInterest
                            ? pointOfInterestFields.find((poiField) => poiField.key === field.key)
                            : getFieldFromKey(field.key ?? '')
                      }
                      getOptionLabel={(option) => formatEnumForDisplay(option.name)}
                      onChange={(_, value) => {
                        setSelectedType({
                          ...selectedType,
                          ...(selectedType?.markerLabelConfig
                            ? {
                                markerLabelConfig: {
                                  ...selectedType?.markerLabelConfig,
                                  fields: (selectedType?.markerLabelConfig?.fields ?? [])?.map(
                                    (field, i) => {
                                      if (i === index) {
                                        return {
                                          ...field,
                                          key: value?.key ?? '',
                                        };
                                      }
                                      return field;
                                    }
                                  ),
                                },
                              }
                            : {
                                markerLabelConfig: {
                                  type:
                                    selectedType?.markerLabelConfig?.type ??
                                    MarkerLabelConfigTypeEnum.Compact,
                                  hideName: selectedType?.markerLabelConfig?.hideName ?? false,
                                  fields: [],
                                  headerField: undefined,
                                },
                              }),
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="standard" placeholder="" />
                      )}
                      sx={{
                        ...dropDownStyles,
                        width: '100%',
                      }}
                    />
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<AbyssIcon iconName="delete" />}
                    disabled={mode === 'ReadOnly'}
                    onClick={() => {
                      setSelectedType({
                        ...selectedType,
                        ...(selectedType?.markerLabelConfig
                          ? {
                              markerLabelConfig: {
                                ...selectedType?.markerLabelConfig,
                                fields: (selectedType?.markerLabelConfig?.fields ?? [])?.filter(
                                  (field, i) => i !== index
                                ),
                              },
                            }
                          : {
                              markerLabelConfig: {
                                type:
                                  selectedType?.markerLabelConfig?.type ??
                                  MarkerLabelConfigTypeEnum.Compact,
                                hideName: selectedType?.markerLabelConfig?.hideName ?? false,
                                fields: [],
                                headerField: undefined,
                              },
                            }),
                      });
                    }}
                  >
                    Delete
                  </Button>
                </Stack>
              ))}
              <Button
                sx={{ width: 'fit-content' }}
                variant="contained"
                disabled={mode === 'ReadOnly'}
                startIcon={<Add />}
                onClick={() => {
                  setSelectedType({
                    ...selectedType,
                    ...(selectedType?.markerLabelConfig
                      ? {
                          markerLabelConfig: {
                            ...selectedType?.markerLabelConfig,
                            fields: [
                              ...(selectedType?.markerLabelConfig?.fields ?? []),
                              {
                                type: MarkerLabelFieldTypeEnum.TemplateField,
                                key: '',
                              },
                            ],
                          },
                        }
                      : {
                          markerLabelConfig: {
                            type:
                              selectedType?.markerLabelConfig?.type ??
                              MarkerLabelConfigTypeEnum.Compact,
                            hideName: selectedType?.markerLabelConfig?.hideName ?? false,
                            fields: [
                              ...(selectedType?.markerLabelConfig?.fields ?? []),
                              {
                                type: MarkerLabelFieldTypeEnum.TemplateField,
                                key: '',
                              },
                            ],
                            headerField: undefined,
                          },
                        }),
                  });
                }}
              >
                Add Field
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <Stack sx={{ flex: 1 }}>
          <Typography variant="h4">Preview</Typography>
          <Stack justifyContent="center" alignItems="center" width="100%" paddingTop="20rem">
            {examplePoi ? (
              <PointOfInterestMarkerLabel
                pointOfInterest={examplePoi}
                scale={1.5}
                customTemplate={selectedType}
              />
            ) : (
              <Typography variant="caption">
                Only available when {selectedType?.name} POIs already exist
              </Typography>
            )}
          </Stack>
        </Stack>
      </FullWidthPanel>
    </>
  );
};
